import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import VideoTile from './VideoTile';
import './LiveStudio.css';

const REACTIONS = ['👏', '🔥', '❤️', '👍', '😂', '🎉'];

const Icon = {
  mic: (off) => off ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V8a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 14v-2"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
  ),
  cam: (off) => off ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
  ),
  screen: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  hand: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 11V6a2 2 0 0 0-4 0v7"/><path d="M14 10V4a2 2 0 0 0-4 0v8"/><path d="M10 9.5V5a2 2 0 0 0-4 0v9"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-1.4-5.5-2.8"/></svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
  ),
  full: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
  ),
  grid: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
  ),
  leave: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  ),
  smile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
  ),
};

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

let emojiSeq = 0;

const LiveStudio = ({
  roomInfo,
  tiles,
  chatMessages,
  chatInput,
  onChatInput,
  onSendChat,
  isMicOn,
  isCameraOn,
  isSharing,
  handRaised,
  unreadChat,
  isHost,
  shareUrl,
  meetingId,
  copied,
  toast,
  devices = { audio: [], video: [] },
  selectedDevices,
  onSelectDevice,
  onToggleMic,
  onToggleCam,
  onToggleShare,
  onToggleHand,
  onReaction,
  onCopyLink,
  onCopyId,
  onLeave,
  onEnd,
  onFullscreen,
  onChatOpen,
  incomingReaction,
}) => {
  const [panel, setPanel] = useState(null);
  const [view, setView] = useState('gallery');
  const [chromeOn, setChromeOn] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [floaters, setFloaters] = useState([]);
  const idleRef = useRef(null);
  const chatEnd = useRef(null);
  const startedAt = useMemo(() => {
    const t = roomInfo?.startedAt ? new Date(roomInfo.startedAt).getTime() : Date.now();
    return Number.isNaN(t) ? Date.now() : t;
  }, [roomInfo?.startedAt]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, panel]);

  const bumpChrome = () => {
    setChromeOn(true);
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => {
      if (!panel && !reactionsOpen && !devicesOpen && !confirm) setChromeOn(false);
    }, 2800);
  };

  useEffect(() => {
    bumpChrome();
    const onMove = () => bumpChrome();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onMove);
      clearTimeout(idleRef.current);
    };
  }, [panel, reactionsOpen, devicesOpen, confirm]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const k = e.key.toLowerCase();
      if (k === 'm') onToggleMic();
      if (k === 'v') onToggleCam();
      if (k === 'c') setPanel((p) => (p === 'chat' ? null : 'chat'));
      if (k === 'p') setPanel((p) => (p === 'people' ? null : 'people'));
      if (k === 'i') setPanel((p) => (p === 'info' ? null : 'info'));
      if (k === 'f') onFullscreen?.();
      if (k === 'escape') {
        setPanel(null);
        setReactionsOpen(false);
        setDevicesOpen(false);
        setConfirm(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToggleMic, onToggleCam, onFullscreen]);

  useEffect(() => {
    if (!incomingReaction?.emoji) return;
    const id = ++emojiSeq;
    setFloaters((f) => [...f, { emoji: incomingReaction.emoji, id, left: 12 + Math.random() * 76 }]);
  }, [incomingReaction]);

  const react = (emoji) => {
    const id = ++emojiSeq;
    setFloaters((f) => [...f, { emoji, id, left: 12 + Math.random() * 76 }]);
    onReaction(emoji);
    setReactionsOpen(false);
  };

  const gridClass = tiles.length <= 1 ? 'ls-grid--1'
    : tiles.length === 2 ? 'ls-grid--2'
      : tiles.length <= 4 ? 'ls-grid--4'
        : tiles.length <= 6 ? 'ls-grid--6'
          : 'ls-grid--more';

  const speaker = tiles.find((t) => t.isScreenShare) || tiles.find((t) => !t.isLocal) || tiles[0];
  const rest = tiles.filter((t) => t.id !== speaker?.id);
  const live = roomInfo?.isLive !== false;

  const togglePanel = (id) => setPanel((p) => {
    const next = p === id ? null : id;
    if (next === 'chat') onChatOpen?.();
    return next;
  });

  return (
    <div className={`ls ${chromeOn ? '' : 'ls--idle'}`} onClick={bumpChrome}>
      <header className="ls-top">
        <div className="ls-top__meta">
          <span className={`ls-pulse ${live ? '' : 'ls-pulse--wait'}`} />
          <h1 className="ls-top__title">{roomInfo?.title || 'QoreTalks meeting'}</h1>
          <span className={`ls-badge ${live ? '' : 'ls-badge--wait'}`}>{live ? 'LIVE' : 'WAITING'}</span>
          <span className="ls-timer">{formatElapsed(now - startedAt)}</span>
        </div>
        <div className="ls-top__actions">
          <button type="button" className="ls-icon" title="Copy invite" onClick={onCopyLink}>{Icon.copy}</button>
          <button type="button" className={`ls-icon ${view === 'gallery' ? 'ls-icon--on' : ''}`} title="Gallery / speaker" onClick={() => setView((v) => (v === 'gallery' ? 'speaker' : 'gallery'))}>{Icon.grid}</button>
          <button type="button" className="ls-icon" title="Fullscreen (F)" onClick={onFullscreen}>{Icon.full}</button>
        </div>
      </header>

      <div className="ls-stage">
        {view === 'speaker' && speaker ? (
          <div className="ls-speaker">
            <div className="ls-speaker__main">
              <VideoTile fill {...speaker} />
            </div>
            {rest.length > 0 && (
              <div className="ls-film">
                {rest.map((tile) => <VideoTile key={tile.id} fill {...tile} />)}
              </div>
            )}
          </div>
        ) : (
          <div className={`ls-grid ${gridClass}`}>
            {tiles.map((tile) => <VideoTile key={tile.id} fill {...tile} />)}
          </div>
        )}
      </div>

      <AnimatePresence>
        {panel && (
          <motion.aside
            className="ls-drawer"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.18 }}
          >
            <div className="ls-drawer__head">
              {panel === 'chat' && 'In-meeting chat'}
              {panel === 'people' && `People · ${tiles.length}`}
              {panel === 'info' && 'Meeting details'}
              <button type="button" className="ls-icon" onClick={() => setPanel(null)} aria-label="Close">{Icon.x}</button>
            </div>
            {panel === 'chat' && (
              <>
                <div className="ls-drawer__body">
                  {chatMessages.length === 0 && <p className="ls-chat__sys">Chat is private to this call. It stays closed until you open it.</p>}
                  {chatMessages.map((msg) => msg.isSystem ? (
                    <p key={msg.id} className="ls-chat__sys">{msg.text}</p>
                  ) : (
                    <div key={msg.id} className="ls-chat__row">
                      <div className="ls-chat__av">{(msg.sender || 'A').charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="ls-chat__name">{msg.sender}</div>
                        <div className="ls-chat__text">{msg.text}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEnd} />
                </div>
                <form className="ls-drawer__foot ls-chat__form" onSubmit={onSendChat}>
                  <input value={chatInput} onChange={(e) => onChatInput(e.target.value)} placeholder="Message everyone…" />
                  <button type="submit" aria-label="Send">{Icon.send}</button>
                </form>
              </>
            )}
            {panel === 'people' && (
              <div className="ls-drawer__body">
                {tiles.map((t) => (
                  <div key={t.id} className="ls-person">
                    <div className="ls-chat__av">{(t.label || '?').charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="ls-person__name">{t.label}{t.isLocal ? ' (You)' : ''}</div>
                      <div className="ls-person__meta">
                        {t.isHost ? 'Host · ' : ''}{t.isMuted ? 'Muted' : 'Mic on'}{t.handRaised ? ' · Hand raised' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {panel === 'info' && (
              <div className="ls-drawer__body ls-info">
                <dt>Meeting ID</dt>
                <dd>{meetingId}</dd>
                <dt>Invite link</dt>
                <dd>{shareUrl}</dd>
                <dt>Host</dt>
                <dd>{roomInfo?.host?.name || 'Host'}</dd>
                <div className="ls-info__actions">
                  <button type="button" className="ls-btn ls-btn--primary" onClick={onCopyLink}>Copy link</button>
                  <button type="button" className="ls-btn ls-btn--ghost" onClick={onCopyId}>Copy ID</button>
                </div>
                <p className="ls-chat__sys" style={{ marginTop: '1rem' }}>Shortcuts: M mute · V camera · C chat · P people · F fullscreen</p>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>

      {reactionsOpen && (
        <div className="ls-react" role="menu">
          {REACTIONS.map((em) => (
            <button key={em} type="button" onClick={() => react(em)}>{em}</button>
          ))}
        </div>
      )}

      {devicesOpen && (
        <div className="ls-devices">
          <label>Microphone</label>
          <select value={selectedDevices?.audio || ''} onChange={(e) => onSelectDevice?.('audio', e.target.value)}>
            {devices.audio.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>)}
          </select>
          <label>Camera</label>
          <select value={selectedDevices?.video || ''} onChange={(e) => onSelectDevice?.('video', e.target.value)}>
            {devices.video.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>)}
          </select>
        </div>
      )}

      <nav className="ls-dock" aria-label="Meeting controls">
        <button type="button" className={`ls-dock__btn ${isMicOn ? '' : 'ls-dock__btn--off'}`} onClick={onToggleMic} onContextMenu={(e) => { e.preventDefault(); setDevicesOpen((v) => !v); }}>
          {Icon.mic(!isMicOn)} {isMicOn ? 'Mute' : 'Unmute'}
        </button>
        <button type="button" className={`ls-dock__btn ${isCameraOn ? '' : 'ls-dock__btn--off'}`} onClick={onToggleCam}>
          {Icon.cam(!isCameraOn)} {isCameraOn ? 'Stop' : 'Start'}
        </button>
        <button type="button" className={`ls-dock__btn ${isSharing ? 'ls-dock__btn--on' : ''}`} onClick={onToggleShare}>
          {Icon.screen} {isSharing ? 'Stop share' : 'Share'}
        </button>
        <span className="ls-dock__sep" />
        <button type="button" className="ls-dock__btn" onClick={() => { setReactionsOpen((v) => !v); setPanel(null); }}>
          {Icon.smile} React
        </button>
        <button type="button" className={`ls-dock__btn ${handRaised ? 'ls-dock__btn--warn' : ''}`} onClick={onToggleHand}>
          {Icon.hand} Hand
        </button>
        <button type="button" className={`ls-dock__btn ${panel === 'people' ? 'ls-dock__btn--on' : ''}`} onClick={() => togglePanel('people')}>
          {Icon.people} People
        </button>
        <button type="button" className={`ls-dock__btn ${panel === 'chat' ? 'ls-dock__btn--on' : ''}`} onClick={() => togglePanel('chat')}>
          {Icon.chat} Chat
          {unreadChat > 0 && panel !== 'chat' && <span className="ls-badge-count">{unreadChat}</span>}
        </button>
        <button type="button" className={`ls-dock__btn ${panel === 'info' ? 'ls-dock__btn--on' : ''}`} onClick={() => togglePanel('info')}>
          {Icon.info} Info
        </button>
        <span className="ls-dock__sep" />
        {isHost && (
          <button type="button" className="ls-dock__btn" onClick={() => setConfirm('end')}>End</button>
        )}
        <button type="button" className="ls-dock__btn ls-dock__btn--leave" onClick={() => setConfirm('leave')}>
          {Icon.leave} Leave
        </button>
      </nav>

      {toast && <div className="ls-toast">{toast}</div>}

      <div className="ls-float">
        {floaters.map((f) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 1, y: 0, scale: 0.6 }}
            animate={{ opacity: 0, y: -140, scale: 1.5 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => setFloaters((list) => list.filter((x) => x.id !== f.id))}
            style={{ position: 'absolute', left: `${f.left}%`, bottom: 0, fontSize: 32 }}
          >
            {f.emoji}
          </motion.div>
        ))}
      </div>

      {confirm && (
        <div className="ls-modal" role="dialog">
          <div className="ls-modal__card">
            <h3>{confirm === 'end' ? 'End meeting for everyone?' : 'Leave this meeting?'}</h3>
            <p>
              {confirm === 'end'
                ? 'All participants will be disconnected and the call will be saved to history.'
                : 'You can rejoin with the same invite link while the host is still here.'}
            </p>
            <div className="ls-modal__actions">
              <button type="button" className="ls-btn ls-btn--ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button
                type="button"
                className={`ls-btn ${confirm === 'end' ? 'ls-btn--danger' : 'ls-btn--primary'}`}
                onClick={() => { confirm === 'end' ? onEnd() : onLeave(); }}
              >
                {confirm === 'end' ? 'End meeting' : 'Leave'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStudio;
export { REACTIONS };
